import { objectKeys, type ReadonlyNonEmptyArray, type ReadonlyRecord } from '../../../../../../utils'

import { areCybusServiceDataResourceEquals, Capability, ConnectwareError, ConnectwareErrorType, CybusServiceDataResourceType } from '../../../../../../domain'

import type { PageSubscriptionsTypes, SubscriptionFilterArgs } from '../../../../../../application'

import { type Resource, type ResourceCategories, TopicsMapper } from '../../../../mappers'
import {
    type AllResourcesRequestConfig,
    BasePageResourceSubscriptionStrategy,
    type PageResourceSortableColumnsMap,
    type ServiceResourcesRequestConfig,
} from '../Resource'

type AllowedCategory = Extract<ResourceCategories, 'Endpoint' | 'Mapping' | 'Node'>

const resourceTypesMap: ReadonlyRecord<AllowedCategory, CybusServiceDataResourceType> = {
    Endpoint: CybusServiceDataResourceType.ENDPOINT,
    Mapping: CybusServiceDataResourceType.MAPPING,
    Node: CybusServiceDataResourceType.NODE,
}

export class ServicesDataResourcesStrategy extends BasePageResourceSubscriptionStrategy<AllowedCategory, 'serviceResources', void> {
    protected override readonly areEquals = areCybusServiceDataResourceEquals

    protected override readonly supportedFilters: (keyof SubscriptionFilterArgs)[] = ['service']

    protected readonly serviceResourcesRequestConfig: ServiceResourcesRequestConfig<'mappings' | 'endpoints' | 'nodes'> = {
        capability: Capability.SERVICE_SUBSCRIPTION_METADATA,
        path: '/api/v2/services/+/resources',
        method: 'GET',
    }

    protected override readonly resourceCategories = objectKeys(resourceTypesMap) as readonly AllowedCategory[] as ReadonlyNonEmptyArray<AllowedCategory>

    protected override readonly serverErrorMessage = 'There was an issue while fetching data resources'

    protected sortingColumns: PageResourceSortableColumnsMap<'serviceResources'> = {
        id: 'resourceId',
        type: 'category',
    }

    override readonly requiredPermissions = [Capability.SERVICE_SUBSCRIPTION_METADATA]

    // eslint-disable-next-line class-methods-use-this
    protected get allResourcesRequestConfig (): AllResourcesRequestConfig<'mappings' | 'endpoints' | 'nodes'> {
        throw new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Fetching service resources without a service is not supported')
    }

    // eslint-disable-next-line class-methods-use-this
    protected override fetchAux (): Promise<void> {
        return Promise.resolve()
    }

    // eslint-disable-next-line class-methods-use-this
    protected override mapResource (resource: Resource<AllowedCategory>): PageSubscriptionsTypes['serviceResources'] {
        const topicsMapper = TopicsMapper.create(resource.category)
        let topics: PageSubscriptionsTypes['serviceResources']['topics']

        switch (resource.category) {
            case 'Endpoint':
            case 'Node':
                topics = topicsMapper.withOperation(resource.resourceConfig.args.operation).mapAll(resource.resourceConfig.args.topic)
                break
            case 'Mapping':
                topics = resource.resourceConfig.args.mappings.flatMap(({ subscribe, publish }) =>
                    [...subscribe, publish].map((ps) => topicsMapper.mapSingle(ps.topic))
                )
                break
        }

        return {
            id: resource.resourceId,
            type: resourceTypesMap[resource.category],
            topics: [...topics].sort(),
        }
    }
}
